import { get, isEmpty, memoize } from 'lodash';
import { ACTIONS } from '../Constants';
import { buildQueryString } from '../utils/StringUtils';
import { canCalculateCompleteness, computeUserLearningAssetCompleteness } from '../utils/UserLearningAssetUtils';
import { VerbFetch } from '../VerbFetch';

// Generate sectionItemById lookup for learningAsset on a userLearningAsset
memoize.Cache = WeakMap;
const weakMemoize = memoize;

const generateSectionItemById = (learningAsset) => {
    const result = {};
    const sections = get(learningAsset, 'sections', []) || [];

    sections.forEach((section) => {
        const sectionItems = get(section, 'sectionItems', []) || [];
        sectionItems.forEach((sectionItem) => {
            result[sectionItem.id] = sectionItem;
        });
    });

    return result;
};

/**
 *
 * @type {Function}
 */
export const cachedGenerateSectionItemById = weakMemoize(generateSectionItemById);

/**
 *
 * @param {Object} params
 * @param {String} actionType
 * @return {function(*)}
 * @private
 */
const _getManyUserLearningAssets = (params, actionType) => {
    const { pathsContext, ...rest } = params;
    const baseUrl = `${pathsContext.serverUrl}/userlearningassets`;
    const url = buildQueryString(rest, baseUrl);
    return (dispatch) => {
        dispatch({
            type: ACTIONS.GET_MANY_USERLEARNINGASSETS_START
        });
        return VerbFetch(url)
            .then((body) => {
                if (!isEmpty(body)) {
                    body.forEach((userLearningAsset) => {
                        if (userLearningAsset.track) {
                            userLearningAsset.track.sectionItemById = cachedGenerateSectionItemById(
                                userLearningAsset.track
                            );
                        }

                        if (canCalculateCompleteness(userLearningAsset)) {
                            userLearningAsset.completeness = computeUserLearningAssetCompleteness(userLearningAsset);
                        }
                    });
                }

                dispatch({
                    type: actionType,
                    payload: {
                        userLearningAssets: body
                    }
                });
                return body;
            })
            .catch(console.error);
    };
};

/**
 *
 * @param {Object} query
 * @return {function(*)}
 */
export const getFilteredUserLearningAssets = (query) =>
    _getManyUserLearningAssets(query, ACTIONS.GET_FILTERED_USERLEARNINGASSETS);

/**
 *
 * @param {Object} params
 * @return {function(*)}
 */
export const getManyUserLearningAssets = (params) =>
    _getManyUserLearningAssets(params, ACTIONS.GET_MANY_USERLEARNINGASSETS);

export const getUserLearningAssetSections = (userLearningAsset) => (dispatch) => {
    dispatch({
        type: ACTIONS.GET_SECTIONS,
        payload: userLearningAsset
    });
};

export const getUserLearningAsset = (params) => {
    const { userLearningAssetId, pathsContext } = params;
    const url = `${pathsContext.serverUrl}/userlearningassets/${userLearningAssetId}`;

    return (dispatch) =>
        VerbFetch(url)
            .then((body) => {
                const userLearningAsset = Object.assign({}, body);
                if (userLearningAsset.track) {
                    userLearningAsset.track.sectionItemById = cachedGenerateSectionItemById(userLearningAsset.track);
                }

                if (canCalculateCompleteness(userLearningAsset)) {
                    userLearningAsset.completeness = computeUserLearningAssetCompleteness(userLearningAsset);
                }

                dispatch({
                    type: ACTIONS.GET_USERLEARNINGASSET,
                    payload: {
                        userLearningAsset
                    }
                });

                return userLearningAsset;
            })
            .then((body) => {
                dispatch({
                    type: ACTIONS.GET_SECTIONS,
                    payload: body
                });

                return body;
            });
};

/**
 *
 * @param {Object} params
 * @return {function(*)}
 */
export const postUserLearningAsset = (params) => {
    const { body, pathsContext } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.POST_USERLEARNINGASSET_START,
            payload: {
                body
            }
        });
        return VerbFetch(`${pathsContext.serverUrl}/userlearningassets`, {
            method: 'POST',
            body
        }).then((body) => {
            const userLearningAsset = Object.assign({}, body);
            if (userLearningAsset.track) {
                userLearningAsset.track.sectionItemById = cachedGenerateSectionItemById(body.track);
            }

            if (canCalculateCompleteness(userLearningAsset)) {
                userLearningAsset.completeness = computeUserLearningAssetCompleteness(userLearningAsset);
            }

            dispatch({
                type: ACTIONS.POST_USERLEARNINGASSET_SUCCESS,
                payload: {
                    userLearningAsset
                }
            });

            return body;
        });
    };
};

export const putUserLearningAsset = (params) => {
    const { userLearningAssetId, body, pathsContext } = params;

    // scrub activity from PUT request since its a readonly property
    delete body.track;

    return (dispatch) => {
        dispatch({
            type: ACTIONS.PUT_USERLEARNINGASSET_START,
            payload: {
                body
            }
        });
        return VerbFetch(`${pathsContext.serverUrl}/userlearningassets/${userLearningAssetId}`, {
            method: 'PUT',
            body
        }).then((body) => {
            const userLearningAsset = Object.assign({}, body);

            if (userLearningAsset.track) {
                userLearningAsset.track.sectionItemById = cachedGenerateSectionItemById(body.track);
            }

            if (canCalculateCompleteness(userLearningAsset)) {
                userLearningAsset.completeness = computeUserLearningAssetCompleteness(userLearningAsset);
            }

            dispatch({
                type: ACTIONS.PUT_USERLEARNINGASSET_SUCCESS,
                payload: {
                    userLearningAsset
                }
            });
            return body;
        });
    };
};

export const putCompletedSectionItem = (params) => {
    const { userLearningAssetId, body, pathsContext } = params;

    return (dispatch) => {
        dispatch({
            type: ACTIONS.PUT_USERLEARNINGASSET_START,
            payload: {
                body
            }
        });
        return VerbFetch(`${pathsContext.serverUrl}/userlearningassets/${userLearningAssetId}/completedsectionitems`, {
            method: 'PUT',
            body
        }).then((updateResult) => {
            dispatch({
                type: ACTIONS.PUT_COMPLETEDSECTIONITEMDATA_SUCCESS,
                payload: {
                    userLearningAssetId,
                    completedSectionItems: updateResult.completedSectionItems,
                    updatedOn: updateResult.updatedOn,
                    status: updateResult.status
                }
            });

            dispatch({
                type: ACTIONS.UPDATE_WORKSPACE_ASSET_METADATA,
                payload: {
                    updatedOn: updateResult.updatedOn,
                    status: updateResult.status
                }
            });
            return updateResult;
        });
    };
};

export const putUserLearningAssetMetadata = (params) => {
    const { userLearningAssetId, body, pathsContext } = params;

    return (dispatch) => {
        dispatch({
            type: ACTIONS.PUT_USERLEARNINGASSET_METADATA_START,
            payload: {
                body
            }
        });

        return VerbFetch(`${pathsContext.serverUrl}/userlearningassets/${userLearningAssetId}/metadata`, {
            method: 'PUT',
            body
        })
            .then((updateResult) => {
                dispatch({
                    type: ACTIONS.PUT_USERLEARNINGASSET_METADATA_SUCCESS,
                    payload: {
                        userLearningAssetId,
                        updatedOn: updateResult.updatedOn,
                        status: updateResult.status
                    }
                });
                dispatch({
                    type: ACTIONS.UPDATE_WORKSPACE_ASSET_METADATA,
                    payload: {
                        updatedOn: updateResult.updatedOn,
                        status: updateResult.status
                    }
                });
                return updateResult;
            })
            .catch((err) => console.log('Metadata error: ', err));
    };
};

export const getLearningAssetRecommended = (params) => {
    const { userLearningAssetId, pathsContext } = params;
    const url = `${pathsContext.serverUrl}/userlearningassets/${userLearningAssetId}/recommended`;
    return (dispatch) =>
        VerbFetch(url)
            .then((body) => {
                dispatch({
                    type: ACTIONS.GET_INTERSTITIAL_RECOMMENDATION,
                    payload: {
                        recommendedData: body
                    }
                });
                return body;
            })
            .catch((err) => {
                console.error(err);
            });
};

export const createTemporaryUserLearningAsset = (params) => {
    const { userLearningAsset } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.CREATE_TEMPORARY_USERLEARNINGASSET,
            payload: {
                userLearningAsset
            }
        });
    };
};

export const removeTemporaryUserLearningAsset = (params) => {
    const { userLearningAssetId } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.REMOVE_TEMPORARY_USERLEARNINGASSET,
            payload: {
                userLearningAssetId
            }
        });
    };
};

export const resetUserLearningAsset = (params) => {
    const { userLearningAssetId, body, pathsContext } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.RESET_USERLEARNINGASSET_START,
            payload: {
                body
            }
        });

        return VerbFetch(
            `${pathsContext.adminServerUrl}/userlearningassets/${userLearningAssetId}/completedsectionitems`,
            {
                method: 'DELETE',
                body
            }
        ).then((body) => {
            dispatch({
                type: ACTIONS.RESET_USERLEARNINGASSET_SUCCESS,
                payload: {
                    userLearningAsset: body
                }
            });
        });
    };
};

export const clearSearchedUserLearningAssets = () => (dispatch) =>
    dispatch({
        type: ACTIONS.CLEAR_SEARCHED_USERLEARNINGASSETS
    });

export const clearUserLearningAssets = () => (dispatch) =>
    dispatch({
        type: ACTIONS.CLEAR_USERLEARNINGASSETS
    });

export const getUserLearningAssetReviewAnswers = (pathsContext, { userLearningAssetId }) =>  
    VerbFetch(`${pathsContext.serverUrl}/userlearningassets/${userLearningAssetId}/reviewanswers`);

export const getLearningAssetIdFromUserLearningAssetId = (pathsContext, { userLearningAssetId }) =>
    VerbFetch(`${pathsContext.serverUrl}/userlearningassets/${userLearningAssetId}/learningassetid`);
